import { format } from "date-fns";
import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import colors from "../../assets/colors";
import { getAppConfig } from "../../config";
import {
  updateClientCallsLeft,
  updateClientSalesPackageEndDate,
  updateInitialTermClientCoachingPackageStartDate,
} from "../../redux/actions/ClientDetail";
import { StoreState } from "../../redux/reducers";
import {
  MutationUpdateClientSalesPackageEndDateArgs,
  MutationUpdateInitialTermClientCoachingPackageStartDateArgs,
  User,
  UserType,
} from "../../types/gqlTypes";
import EditIcon from "../Icons/EditIcon";
import SimpleButton from "../SimpleButton";
import { CoachingPackageDateField } from "./CoachingPackageDateField";

interface Props {
  startDate: string;
  packageEndDate: string;
  callsLeft: number;
  liveTrainingRemainingSessions: number;
  updateClientCallsLeft: (clientId: string, callsLeft: number) => void;
  updateInitialTermClientCoachingPackageStartDate: (
    args: MutationUpdateInitialTermClientCoachingPackageStartDateArgs
  ) => void;
  updateClientSalesPackageEndDate: (
    args: MutationUpdateClientSalesPackageEndDateArgs
  ) => void;
  user: User;
}

const ServiceTracker = (props: Props) => {
  const {
    startDate,
    packageEndDate,
    callsLeft,
    liveTrainingRemainingSessions,
    updateClientCallsLeft,
    updateInitialTermClientCoachingPackageStartDate,
    updateClientSalesPackageEndDate,
    user,
  } = props;
  const { id: clientId } = useParams();

  const [editingCallsLeft, setEditingCallsLeft] = useState(null);

  const inputRef = useRef(null);

  const onSaveCallsLeft = () => {
    updateClientCallsLeft(clientId, Number(editingCallsLeft) || 0);
    setEditingCallsLeft(null);
  };

  const onSavePackageEndDate = (date: Date) => {
    updateClientSalesPackageEndDate({
      clientId,
      packageEndDate: format(date, "yyyy-MM-dd"),
    } as MutationUpdateClientSalesPackageEndDateArgs);
  };

  const onSaveStartDate = (date: Date) => {
    updateInitialTermClientCoachingPackageStartDate({
      clientId,
      startDate: format(date, "yyyy-MM-dd"),
    });
  };

  const editCallsLeftInput = (
    <input
      ref={inputRef}
      value={editingCallsLeft}
      onChange={(event) => {
        const { value } = event.target;
        if ((Number(value) >= 0 && Number(value) <= 100) || value === "") {
          setEditingCallsLeft(value);
        }
      }}
      onKeyDown={(event) => {
        const { key } = event;
        if (key === "Enter") {
          onSaveCallsLeft();
        }
        if (key !== "Backspace" && isNaN(Number(key))) {
          event.preventDefault();
        }
      }}
      onBlur={() => {
        onSaveCallsLeft();
      }}
      style={{
        textAlign: "center",
        backgroundColor: "#F3F3F5",
        color: colors.caliber_secondary,
        borderRadius: 4,
        borderColor: colors.caliber_white,
        boxShadow: "none",
        border: "none",
        borderWidth: 0,
        width: 30,
        height: 20,
        fontSize: "12px",
      }}
    />
  );

  const callsLeftDiv = (
    <div className="d-flex">
      <SimpleButton
        onClick={() => {
          setEditingCallsLeft(callsLeft?.toString() || "0");
          setTimeout(() => {
            inputRef?.current?.focus();
          }, 0);
        }}
        className="d-flex align-items-center justify-content-center"
        style={{
          marginRight: 6,
        }}
      >
        <EditIcon />
      </SimpleButton>
      <div
        className="bold"
        style={{
          padding: "1px 10px",
          borderRadius: 4,
          fontSize: "12px",
          backgroundColor: colors.caliber_white,
          color: colors.caliber_secondary,
        }}
      >
        {callsLeft || 0}
      </div>
    </div>
  );

  return (
    <div>
      <div
        style={{
          color: colors.caliber_gray_border,
          marginTop: 15,
        }}
        className="paragraph-small"
      >
        Service tracker
      </div>

      <div
        style={{ margin: "12px 0px" }}
        className="d-flex justify-content-between align-items-center"
      >
        <div
          className="medium-bold"
          style={{
            fontSize: "12px",
            lineHeight: "16px",
            color: colors.caliber_secondary,
          }}
        >
          Strategy calls left
        </div>
        {typeof editingCallsLeft === "string"
          ? editCallsLeftInput
          : callsLeftDiv}
      </div>
      {getAppConfig().liveTrainingSessionsEnabled && (
        <div
          style={{ margin: "12px 0px" }}
          className="d-flex justify-content-between align-items-center"
        >
          <div
            className="medium-bold"
            style={{
              fontSize: "12px",
              lineHeight: "16px",
              color: colors.caliber_secondary,
            }}
          >
            Live training sessions left
          </div>
          <div
            className="bold"
            style={{
              padding: "1px 10px",
              borderRadius: 4,
              fontSize: "12px",
              backgroundColor: colors.caliber_white,
              color: colors.caliber_secondary,
            }}
          >
            {liveTrainingRemainingSessions || 0}
          </div>
        </div>
      )}
      {startDate && (
        <CoachingPackageDateField
          name="Package start date"
          dateString={startDate}
          allowEdit={user.type !== UserType.Trainer}
          onSaveDate={onSaveStartDate}
        />
      )}
      <CoachingPackageDateField
        name="Package end date"
        dateString={packageEndDate}
        allowEdit={user.type !== UserType.Trainer}
        onSaveDate={onSavePackageEndDate}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  updateClientCallsLeft: (clientId: string, callsLeft: number) => {
    dispatch(updateClientCallsLeft(clientId, callsLeft));
  },
  updateClientSalesPackageEndDate: (
    args: MutationUpdateClientSalesPackageEndDateArgs
  ) => {
    dispatch(updateClientSalesPackageEndDate(args));
  },
  updateInitialTermClientCoachingPackageStartDate: (
    args: MutationUpdateInitialTermClientCoachingPackageStartDateArgs
  ) => {
    dispatch(updateInitialTermClientCoachingPackageStartDate(args));
  },
});

const mapStateToProps = (state: StoreState) => ({
  callsLeft: state.clientDetailState.callsLeft,
  liveTrainingRemainingSessions:
    state.clientDetailState.liveTrainingRemainingSessions,
  startDate: state.clientDetailState.startDate,
  packageEndDate: state.clientDetailState.packageEndDate,
  user: state.authenticatedUserState.user,
});

export default connect(mapStateToProps, mapDispatchToProps)(ServiceTracker);
